/**
 * Recipes into sound. The only file in this layer that touches Web Audio nodes.
 *
 * Everything above it decides — which sound, how loud, how muffled, which way.
 * This carries the decision out, and it is deliberately stupid: it has no
 * opinion about distance, walls, rooms or the game, and it cannot form one,
 * because none of that reaches it. It is handed a recipe from `voices.js` and a
 * gain, and it makes a noise.
 *
 * Which is also the shape of the swap. When recorded audio replaces `voices.js`
 * the change here is that a source is a decoded buffer instead of a burst of
 * filtered noise, and every other line in this file already does the right
 * thing with it.
 *
 * ---------------------------------------------------------------------------
 * IT NEVER THROWS
 *
 * There is no audio device in a headless browser, there is no `AudioContext`
 * before a user gesture, and a browser is allowed to refuse one at any time for
 * reasons that are none of our business. A game that dies because it could not
 * make a noise is a far worse bug than a game that is silent, and every script
 * that verifies this project fails on a single console error. So every entry
 * point here is a no-op on a missing context and the whole layer degrades to
 * silence rather than to a stack trace.
 * ---------------------------------------------------------------------------
 */

/** Seconds of noise, generated once and reused by every voice that wants some. */
const NOISE_SECONDS = 2.2;

export function createSynth(context, { destination = null } = {}) {
  /*
   * One buffer of white noise for the life of the page.
   *
   * A footstep is a fortieth of a second of noise through a filter, and
   * generating one each time means allocating and filling a buffer several
   * times a second for the whole shift. Instead there is one, and each voice
   * starts at a random offset into it — which is also where the variation
   * between footsteps comes from, free, and why two steps in a row do not sound
   * like a loop of one step.
   */
  let noiseBuffer = null;
  function noise() {
    if (!context) return null;
    if (noiseBuffer) return noiseBuffer;
    const frames = Math.floor(context.sampleRate * NOISE_SECONDS);
    const buffer = context.createBuffer(1, frames, context.sampleRate);
    const data = buffer.getChannelData(0);
    /*
     * Seeded rather than `Math.random`, so a recording of a run sounds the same
     * twice. Nothing depends on it, but a sound layer that is different every
     * time is a sound layer nobody can compare against itself.
     */
    let seed = 0x9e3779b9;
    for (let i = 0; i < frames; i += 1) {
      seed = (seed * 1664525 + 1013904223) >>> 0;
      data[i] = (seed / 0xffffffff) * 2 - 1;
    }
    noiseBuffer = buffer;
    return buffer;
  }

  const out = destination ?? context?.destination ?? null;

  /**
   * One voice.
   *
   * `gain` is everything the layers above decided, multiplied together and
   * handed down as a single number. `hz` is the wall. `at` is where it is, in
   * the listener's own frame — right, up, ahead — which is what the panner
   * wants and what `falloff.bearing` produces.
   */
  function play(recipe, { gain = 1, hz = 18000, at = null, rate = 1, when = 0 } = {}) {
    if (!context || !out || !recipe || gain <= 0) return null;

    const now = context.currentTime + Math.max(0, when);
    const attack = recipe.attack ?? 0.005;
    const hold = recipe.hold ?? 0;
    const decay = recipe.decay ?? 0.1;
    const trim = (recipe.gain ?? 1) * gain;
    const loop = Boolean(recipe.loop);

    // --- the tail of the chain, shared by every source in this voice --------
    const envelope = context.createGain();
    envelope.gain.value = 0;

    const wall = context.createBiquadFilter();
    wall.type = 'lowpass';
    wall.frequency.value = Math.max(80, hz);
    wall.Q.value = 0.7;

    envelope.connect(wall);

    let head = wall;
    if (at) {
      const panner = context.createPanner();
      panner.panningModel = 'equalpower';
      /*
       * The panner does direction and nothing else. Distance is already decided
       * — by the simulation's own radius, in `falloff.js` — and letting a
       * second attenuation curve run underneath it would mean the volume the
       * player learns the radius from is the product of the rule and a default
       * nobody chose. `rolloffFactor: 0` is the linear model with the slope
       * taken out: pan, no fade.
       */
      panner.distanceModel = 'linear';
      panner.rolloffFactor = 0;
      panner.refDistance = 1;
      panner.maxDistance = 10000;
      // The listener sits at the origin facing -z in Web Audio's frame, so
      // "ahead" is -z and "right" is +x.
      setPosition(panner, at.right, at.up ?? 0, -at.ahead);
      wall.connect(panner);
      head = panner;
    }
    head.connect(out);

    const stops = [];

    if (recipe.noise) {
      const buffer = noise();
      if (buffer) {
        const source = context.createBufferSource();
        source.buffer = buffer;
        source.loop = true;
        source.playbackRate.value = rate;
        const filter = context.createBiquadFilter();
        filter.type = recipe.noise.type ?? 'bandpass';
        filter.Q.value = recipe.noise.q ?? 1;
        const from = recipe.noise.hz ?? 1000;
        filter.frequency.setValueAtTime(from, now);
        if (recipe.noise.to && recipe.noise.to !== from) {
          filter.frequency.exponentialRampToValueAtTime(
            Math.max(20, recipe.noise.to),
            now + attack + hold + decay
          );
        }
        source.connect(filter);
        filter.connect(envelope);
        // A different slice of the buffer each time, which is the variation.
        source.start(now, Math.random() * (NOISE_SECONDS - 0.4));
        stops.push(source);
      }
    }

    for (const tone of recipe.tones ?? []) {
      const oscillator = context.createOscillator();
      oscillator.type = tone.type ?? 'sine';
      const begins = now + (tone.delay ?? 0);
      oscillator.frequency.setValueAtTime(tone.hz * rate, begins);
      if (tone.to && tone.to !== tone.hz) {
        oscillator.frequency.exponentialRampToValueAtTime(
          Math.max(20, tone.to * rate),
          begins + decay
        );
      }
      const level = context.createGain();
      level.gain.value = tone.gain ?? 0.3;
      oscillator.connect(level);
      level.connect(envelope);
      oscillator.start(begins);
      stops.push(oscillator);
    }

    if (!stops.length) {
      head.disconnect();
      return null;
    }

    // --- the shape of it ----------------------------------------------------
    const peak = Math.max(1e-4, trim);
    envelope.gain.setValueAtTime(0, now);
    envelope.gain.linearRampToValueAtTime(peak, now + attack);
    if (loop) {
      // Something that runs until it is told to stop holds at full.
      if (hold > 0) envelope.gain.setValueAtTime(peak, now + attack + hold);
    } else {
      envelope.gain.setValueAtTime(peak, now + attack + hold);
      envelope.gain.exponentialRampToValueAtTime(1e-4, now + attack + hold + decay);
    }

    let done = false;
    const cleanup = () => {
      if (done) return;
      done = true;
      for (const source of stops) {
        try {
          source.stop();
        } catch {
          // Already stopped, or never started. Either way there is nothing to do.
        }
      }
      try {
        head.disconnect();
      } catch {
        // Disconnected with the context, which is the same outcome.
      }
    };

    if (!loop) {
      const ends = now + attack + hold + decay + 0.05;
      for (const source of stops) {
        if (source.stop) {
          try {
            source.stop(ends);
          } catch {
            // A source that refuses a stop time gets stopped by the timer below.
          }
        }
      }
      setTimeout(cleanup, (ends - context.currentTime + 0.1) * 1000);
    }

    return {
      /** Moves it, for something that is still going. */
      move(next) {
        if (done || !at || !next) return;
        const panner = head;
        if (panner.positionX !== undefined || panner.setPosition) {
          setPosition(panner, next.right, next.up ?? 0, -next.ahead);
        }
      },
      /** Changes how loud and how muffled it is, without restarting it. */
      set({ gain: nextGain, hz: nextHz, rate: nextRate } = {}) {
        if (done) return;
        const t = context.currentTime;
        if (nextGain !== undefined) {
          // Ramped, never set: a step change on a running voice is a click, and
          // a cart rounding a corner would tick every frame.
          envelope.gain.cancelScheduledValues(t);
          envelope.gain.setTargetAtTime(Math.max(1e-4, (recipe.gain ?? 1) * nextGain), t, 0.04);
        }
        if (nextHz !== undefined) wall.frequency.setTargetAtTime(Math.max(80, nextHz), t, 0.06);
        if (nextRate !== undefined) {
          for (const source of stops) {
            if (source.playbackRate) source.playbackRate.setTargetAtTime(nextRate, t, 0.08);
          }
        }
      },
      /** Lets it go, over its own decay so nothing cuts out. */
      stop(fade = decay) {
        if (done) return;
        const t = context.currentTime;
        envelope.gain.cancelScheduledValues(t);
        envelope.gain.setTargetAtTime(1e-4, t, Math.max(0.02, fade / 3));
        setTimeout(cleanup, (fade + 0.15) * 1000);
      },
      get playing() {
        return !done;
      },
    };
  }

  return {
    play,
    get context() {
      return context;
    },
  };
}

/**
 * Puts a panner somewhere, whichever of the two APIs this browser has.
 *
 * `setPosition` is deprecated and still the only one some builds implement;
 * the `AudioParam` form is the current one and the only one that can be ramped.
 * Both are in the wild and neither is worth an opinion.
 */
function setPosition(panner, x, y, z) {
  if (panner.positionX) {
    panner.positionX.value = x;
    panner.positionY.value = y;
    panner.positionZ.value = z;
    return;
  }
  panner.setPosition?.(x, y, z);
}
